import type { FileUploadItem } from "@Reducers/minimiseFileUpload";

export interface ReconciliationRow {
  fileName: string;
  location: string;
  fileType: string;
}

export interface ReconciliationCounts {
  emptyContainersCount: number;
  unmatchedFilesCount: number;
  hasGaps: boolean;
}

export interface ReconciliationRows {
  emptyContainers: ReconciliationRow[];
  unmatchedFiles: ReconciliationRow[];
}

export interface DirectImportContainer {
  name?: string;
  fileFormat?: string;
  folderPath?: string;
}

type ContainerLike = DirectImportContainer | Record<string, unknown> | null | undefined;

export const containerKey = (container: ContainerLike): string =>
  `${String(container?.name ?? "")}|${String(container?.fileFormat ?? "")}|${String(container?.folderPath ?? "")}`;

const getExtension = (name: string): string => {
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1) : "";
};

const toArray = <T>(value: T[]): T[] => (Array.isArray(value) ? value : []);

/**
 * The upload queue as this module is willing to trust it. Redux declares the slice
 * `FileUploadItem[]`, but rows are written into it by index (`list[index] = { … }` in
 * `reducers/minimiseFileUpload.ts`, with no length check), which leaves holes, and `tsconfig`'s
 * `noUncheckedIndexedAccess` models exactly that everywhere else in this feature — e.g.
 * `FilesSlickGrid`'s own `fileUploadList[index] || {}`. Every predicate below has always guarded
 * against a missing row; widening the element type here is what makes those guards honest instead
 * of laundering them through `Boolean(...)`.
 */
type MaybeFileUploadItem = FileUploadItem | undefined;

const buildMatchedContainerKeys = (files: MaybeFileUploadItem[]): Set<string> =>
  new Set(
    files
      .filter(
        (file): file is FileUploadItem =>
          file != null && !file.isDeleted && !file.error && Boolean(file.matchedDirectImportContainer),
      )
      .map((file) => containerKey(file.matchedDirectImportContainer)),
  );

const isEmptyContainer = (container: DirectImportContainer | undefined, matchedKeys: Set<string>): boolean =>
  container != null && !matchedKeys.has(containerKey(container));

const isUnmatchedFile = (file: MaybeFileUploadItem): file is FileUploadItem =>
  file != null && !file.isDeleted && !file.isFolder && !file.matchedDirectImportContainer;

export const getReconciliationCounts = (
  fileUploadList: FileUploadItem[] = [],
  directImportContainers: DirectImportContainer[] = [],
): ReconciliationCounts => {
  const files: MaybeFileUploadItem[] = toArray(fileUploadList);
  const matchedKeys = buildMatchedContainerKeys(files);
  const emptyContainersCount = toArray<DirectImportContainer | undefined>(directImportContainers).filter((container) =>
    isEmptyContainer(container, matchedKeys),
  ).length;
  const unmatchedFilesCount = files.filter(isUnmatchedFile).length;
  return {
    emptyContainersCount,
    unmatchedFilesCount,
    hasGaps: emptyContainersCount > 0 || unmatchedFilesCount > 0,
  };
};

export const getReconciliationRows = (
  fileUploadList: FileUploadItem[] = [],
  directImportContainers: DirectImportContainer[] = [],
): ReconciliationRows => {
  const files: MaybeFileUploadItem[] = toArray(fileUploadList);
  const matchedKeys = buildMatchedContainerKeys(files);
  const emptyContainers: ReconciliationRow[] = toArray<DirectImportContainer | undefined>(directImportContainers)
    .filter((container): container is DirectImportContainer => isEmptyContainer(container, matchedKeys))
    .map((container) => ({
      fileName: container.name ?? "",
      location: container.folderPath ?? "",
      fileType: String(container.fileFormat ?? "").toUpperCase(),
    }));
  const unmatchedFiles: ReconciliationRow[] = files.filter(isUnmatchedFile).map((file) => ({
    fileName: file.name ?? "",
    location: file.uploadLocation ?? "",
    fileType: getExtension(file.name ?? "").toUpperCase(),
  }));
  return { emptyContainers, unmatchedFiles };
};
